import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useReactToPrint } from "react-to-print";
import { Badge } from "@/components/ui/badge";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Button } from "@akuna/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeletePetVaccination } from "@/hooks/usePetApplications";
import { useExamAttachments } from "@/hooks/useExamAttachments";
import { usePatientHistoryFeed } from "@/hooks/usePatientHistoryFeed";
import { useVisibleClinics } from "@/hooks/useVisibleClinics";
import { resolveHistoryClinicScope } from "@/lib/clinics/visible-clinics";
import { ClinicTag } from "@/components/clinics/ClinicTag";
import type { AppointmentWithDetails } from "@/hooks/useAppointments";
import {
  useMarkTermPrinted,
  useSoftDeletePatientTerm,
  useTermTemplates,
  useUploadTermSignedFile,
  useUpdatePatientTerm,
  type PatientTerm,
} from "@/hooks/usePatientTerms";
import { PrescriptionViewDialog } from "@/components/prescriptions/PrescriptionViewDialog";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import { PrescriptionComposer } from "@/components/prescriptions/PrescriptionComposer";
import { PrescriptionDialogLayout } from "@/components/prescriptions/editor/PrescriptionDialogLayout";
import { VaccinationFormDialog } from "@/components/applications/ApplicationFormDialog";
import { AppointmentDetailsDialog } from "@/components/calendar/AppointmentDetailsDialog";
import { DocumentDeleteConfirmDialog } from "@/components/pets/DocumentDeleteConfirmDialog";
import { DocumentEditDialog } from "@/components/pets/DocumentEditDialog";
import type { PrescriptionWithItems } from "@/hooks/useMedicalPrescriptions";
import {
  formatDateBR,
  parseISODateOnlyLocal,
  parseISODateOrDateTime,
  toISODateOnlyLocal,
} from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import { normalizeStoragePath } from "@/lib/utils/storage";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import {
  FileText,
  Calendar,
  AlertTriangle,
  Download,
  Loader2,
  MoreVertical,
  Pencil,
  ShieldCheck,
  Trash2,
  Eye,
  Printer,
  UploadCloud,
  Send,
} from "lucide-react";
import type { PatientDayEventType } from "@/components/calendar/types/patient-day-events";
import { TermViewDialog } from "@/components/terms/TermViewDialog";
import { TermShareDrawer } from "@/components/terms/TermShareDrawer";
import { SendForSignatureDialog } from "@/components/terms/SendForSignatureDialog";
import { deriveRequiredSignatories } from "@/lib/terms/signature-requirements";
import { TermEditDialog } from "@/components/terms/TermEditDialog";
import { sanitizeRichTextHtml } from "@/components/terms/rich-text-utils";
import { useDocumentTypes } from "@/hooks/useLookups";
import {
  APPOINTMENT_STATUS_LABEL,
} from "@/components/patients/appointment-ui-meta";
import { getTimelineTypeIcon } from "@/lib/event-icons";
import { useClinicAppointmentTypes } from "@/hooks/useClinicAppointmentTypes";
import { renderTermWithTokenValues } from "@/lib/terms/config";
import { downloadPdf } from "@/lib/pdf/download";
import { usePrintSettings } from "@/hooks/usePrintSettings";
import { useUserA1Certificate } from "@/hooks/useUserA1Certificate";
import { useIsVeterinarian } from "@/hooks/useIsVeterinarian";
import { useSignPdf } from "@/hooks/useSignPdf";
import { getGroomingStatusLabel } from "@/lib/utils/grooming-status";
import { formatStayHistoryLabel } from "@/lib/daycare-hotel/stayHistoryLabel";
import { SignedIndicator } from "@/components/documents/SignedIndicator";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { getStayHistoryFilter } from "./history-stay-filter";
import { toHistoryStayPanelPet } from "./history-stay-dialog";
import { FollowUpDialog } from "@/pages/daycare-hotel/stay/FollowUpDialog";
import type { StayHistoryRow } from "@/hooks/usePatientHistoryFeed";

type TimelineType = "consultation" | "appointment" | "vaccine" | "exam" | "prescription" | "grooming" | "term" | "stay";

export type PatientTimelineSignedInput =
  | { type: "prescription"; signedAt: string | null | undefined }
  | { type: "term"; digitallySignedAt: string | null | undefined; hasUploadedFile: boolean };

export function getPatientTimelineSignedFlags(input: PatientTimelineSignedInput) {
  if (input.type === "prescription") {
    return {
      showPrescriptionSigned: Boolean(input.signedAt),
      showTermSigned: false,
      showTermUploaded: false,
    };
  }
  return {
    showPrescriptionSigned: false,
    showTermSigned: Boolean(input.digitallySignedAt),
    showTermUploaded: input.hasUploadedFile,
  };
}

type GroomingOrderItem = {
  title_snapshot?: string;
  service?: { title?: string };
  item?: { title?: string };
};

type TimelineItem = {
  id: string;
  sourceId: string;
  type: TimelineType;
  date: string;
  timestamp: number;
  medical_record_id: string | null;
  title: string;
  description?: string;
  notes?: string;
  link?: string;
  displayDate: string;
  rawDate?: string;
  rawData?: unknown; // Dados completos do item (vacinação, receita, exame, consulta)
  filePath?: string; // Caminho do arquivo para exames
  storageBucket?: string; // Bucket do storage para exames
  petId?: string | null;
  petName?: string | null;
  veterinarianName?: string | null;
};

/**
 * Extrai a clínica de origem de um item do histórico. Todos os `rawData` das
 * fontes (consulta, agendamento, vacina, exame, receita, banho & tosa, termo)
 * carregam `clinic_id`, então a tag é derivada de forma uniforme no render.
 */
const getTimelineItemClinicId = (item: TimelineItem): string | null => {
  const raw = item.rawData as { clinic_id?: string | null } | null | undefined;
  return raw?.clinic_id ?? null;
};

type MedicalRecordTimelineRow = Database["public"]["Tables"]["medical_records"]["Row"] & {
  profiles?: {
    id?: string | null;
    full_name?: string | null;
  } | null;
};

type FilterType = "all" | "consultations" | "vaccines" | "exams" | "prescriptions" | "grooming" | "terms" | "stay";

const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const FILTER_OPTIONS: { value: Exclude<FilterType, "stay">; labelKey: string }[] = [
  { value: "all", labelKey: "timeline.allEvents" },
  { value: "consultations", labelKey: "timeline.consultations" },
  { value: "vaccines", labelKey: "timeline.application" },
  { value: "exams", labelKey: "timeline.exams" },
  { value: "prescriptions", labelKey: "timeline.prescriptions" },
  { value: "terms", labelKey: "timeline.terms" },
  { value: "grooming", labelKey: "timeline.grooming" },
];

const FILTER_MAP: Record<FilterType, TimelineType[]> = {
  all: ["consultation", "appointment", "vaccine", "exam", "prescription", "grooming", "term", "stay"],
  consultations: ["consultation", "appointment"],
  vaccines: ["vaccine"],
  exams: ["exam"],
  prescriptions: ["prescription"],
  terms: ["term"],
  grooming: ["grooming"],
  stay: ["stay"],
};

const TYPE_LABELS: Record<TimelineType, string> = {
  consultation: "Consulta",
  appointment: "Agendamento",
  vaccine: "Aplicação",
  exam: "Documentos",
  prescription: "Receita",
  term: "Termo",
  grooming: "Banho e tosa",
  stay: "Creche & Hotel",
};

const getPrescriptionTimelineLabelKey = (type?: string | null) => {
  if (type === "manipulated") return "timeline.prescriptionTypes.manipulated";
  if (type === "exam") return "timeline.prescriptionTypes.exam";
  return "timeline.prescriptionTypes.standard";
};

const TIMELINE_TYPE_BADGE: Record<TimelineType, string> = {
  consultation: "bg-gradient-to-r from-blue-500 to-blue-600 text-white",
  appointment: "bg-gradient-to-r from-blue-500 to-blue-600 text-white",
  vaccine: "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white",
  exam: "bg-gradient-to-r from-violet-500 to-violet-600 text-white",
  prescription: "bg-gradient-to-r from-amber-500 to-amber-600 text-white",
  term: "bg-gradient-to-r from-slate-500 to-slate-600 text-white",
  grooming: "bg-gradient-to-r from-pink-500 to-pink-600 text-white",
  stay: "bg-gradient-to-r from-orange-500 to-orange-600 text-white",
};

const formatTimelineDate = (value?: string | null) => {
  if (!value) return "-";
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "-";
  return format(parsed, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
};

const formatTimelineDateTime = (value?: string | null) => {
  if (!value) return "-";
  if (ISO_DATE_ONLY_REGEX.test(value)) {
    return formatTimelineDate(value);
  }
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "-";
  return format(parsed, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
};

const formatTimelineTimeHHmm = (value?: string | null) => {
  if (!value) return "";
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "";
  return format(parsed, "HH:mm", { locale: ptBR });
};

const getFileBaseName = (fileName?: string | null) => {
  if (!fileName) return "";
  const trimmed = fileName.trim();
  if (!trimmed) return "";
  const lastDot = trimmed.lastIndexOf(".");
  if (lastDot <= 0) return trimmed;
  return trimmed.slice(0, lastDot);
};

const resolveTimestamp = (value?: string | null) => {
  const parsed = parseISODateOrDateTime(value);
  if (parsed) {
    return {
      iso: parsed.toISOString(),
      timestamp: parsed.getTime(),
    };
  }
  if (value) {
    const fallback = new Date(value);
    if (!Number.isNaN(fallback.getTime())) {
      return {
        iso: fallback.toISOString(),
        timestamp: fallback.getTime(),
      };
    }
  }
  return {
    iso: "",
    timestamp: 0,
  };
};

const truncate = (value?: string, max = 180) => {
  if (!value) return undefined;
  if (value.length <= max) return value;
  return `${value.slice(0, max).trim()}...`;
};

const formatDate = (value?: string | null) => {
  if (!value) return "";
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "";
  return format(parsed, "dd/MM/yyyy", { locale: ptBR });
};

const findMatchingConsultationForAppointment = (
  appointment: AppointmentWithDetails,
  records: MedicalRecordTimelineRow[],
) => {
  return records.find((record) => record.appointment_id === appointment.id) ?? null;
};

const normalizeHistoryAppointmentRow = (row: Record<string, unknown>): AppointmentWithDetails | null => {
  const relationPets = ((row.appointment_pets as Array<{ pet?: Record<string, unknown> | null }> | null) ?? [])
    .map((relation) => relation?.pet ?? null)
    .filter((pet): pet is Record<string, unknown> => Boolean(pet) && !pet.deleted_at);
  const directPet = row.pet && typeof row.pet === "object" && !(row.pet as { deleted_at?: unknown }).deleted_at
    ? (row.pet as Record<string, unknown>)
    : null;
  const pets = relationPets.length > 0 ? relationPets : directPet ? [directPet] : [];
  const primary = pets.find((pet) => pet.id === row.pet_id) ?? pets[0] ?? null;
  const owner = row.owner && typeof row.owner === "object" ? (row.owner as Record<string, unknown>) : null;

  if (!primary || !owner) return null;

  return {
    ...(row as unknown as AppointmentWithDetails),
    pet: primary,
    pets,
    owner,
    vet: row.vet && typeof row.vet === "object" ? (row.vet as Record<string, unknown>) : null,
    appointment_items: (row.appointment_items as AppointmentWithDetails["appointment_items"]) ?? [],
  };
};

function PatientsTimelineLoading() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="overflow-hidden pb-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-20 rounded-full" />
            <Skeleton className="h-9 w-28 rounded-full" />
            <Skeleton className="h-9 w-24 rounded-full" />
            <Skeleton className="h-9 w-24 rounded-full" />
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="relative flex gap-2">
            <div className="flex w-8 shrink-0 flex-col items-center">
              <Skeleton className="h-8 w-8 rounded-full" />
              {index !== 3 ? <span className="mt-2 h-full w-px bg-border" /> : null}
            </div>
            <div className="flex-1 space-y-3">
              <Skeleton className="mt-2 h-4 w-40" />
              <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-6 w-20 rounded-full" />
                  </div>
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-full max-w-2xl" />
                  <Skeleton className="h-4 w-2/3 max-w-xl" />
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface PatientHistoryTimelineProps {
  petId?: string;
  petIds?: string[];
  clinicId?: string | null;
  includeGrooming?: boolean;
  timeScope?: "past" | "future" | "all";
  hideFutureDetails?: boolean;
  initialOpenEvent?: { type: PatientDayEventType; sourceId: string };
  showPetName?: boolean;
}

const PatientHistoryTimeline = ({
  petId,
  petIds,
  clinicId,
  includeGrooming = true,
  timeScope = "all",
  hideFutureDetails = false,
  initialOpenEvent,
  showPetName = false,
}: PatientHistoryTimelineProps) => {
  const { t } = useTranslation("patients");
  const { mode: stayMode } = useDaycareHotelAccess();
  const { data: clinicAppointmentTypes = [] } = useClinicAppointmentTypes();
  const groomingTypeIds = useMemo(
    () => clinicAppointmentTypes.filter((t) => t.behavior === "banho_tosa").map((t) => t.id),
    [clinicAppointmentTypes],
  );
  const { data: printSettings } = usePrintSettings();
  const resolvedPetIds = useMemo(
    () => Array.from(new Set([petId, ...(petIds ?? [])].filter(Boolean))),
    [petId, petIds],
  ) as string[];
  // Consolidação de histórico: lê de todas as clínicas que o usuário acessa no
  // customer atual. O `clinicId` (clínica ativa) permanece para escritas/uploads.
  const { visibleClinicIds } = useVisibleClinics();
  const historyClinicIds = useMemo(
    () => resolveHistoryClinicScope(visibleClinicIds, clinicId),
    [visibleClinicIds, clinicId],
  );
  const isMultiPetMode = !petId && resolvedPetIds.length > 0;
  const hasHandledInitialOpenEvent = useRef(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    hasHandledInitialOpenEvent.current = false;
  }, [initialOpenEvent?.sourceId, initialOpenEvent?.type]);

  const queryClient = useQueryClient();
  const deleteVaccination = useDeletePetVaccination();
  const [selectedFilters, setSelectedFilters] = useState<FilterType[]>(["all"]);
  const [selectedPetId, setSelectedPetId] = useState<string>("all");
  const [selectedVaccination, setSelectedVaccination] = useState<(typeof resolvedVaccinations)[number] | null>(null);
  const [isVaccinationModalOpen, setIsVaccinationModalOpen] = useState(false);
  const [isVaccinationEditModalOpen, setIsVaccinationEditModalOpen] = useState(false);
  const [isDeleteVaccinationDialogOpen, setIsDeleteVaccinationDialogOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState<PrescriptionWithItems | null>(null);
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);
  const [isPrescriptionEditModalOpen, setIsPrescriptionEditModalOpen] = useState(false);
  const [prescriptionEditFooterEl, setPrescriptionEditFooterEl] = useState<HTMLDivElement | null>(null);
  const [autoOpenPrescriptionShare, setAutoOpenPrescriptionShare] = useState(false);
  const [isDeletePrescriptionDialogOpen, setIsDeletePrescriptionDialogOpen] = useState(false);
  const [isDeletingPrescription, setIsDeletingPrescription] = useState(false);
  const [examToDelete, setExamToDelete] = useState<(typeof resolvedExamAttachments)[number] | null>(null);
  const [isDeletingExam, setIsDeletingExam] = useState(false);
  const [examToEdit, setExamToEdit] = useState<(typeof resolvedExamAttachments)[number] | null>(null);
  const [examTitleDraft, setExamTitleDraft] = useState("");
  const [examTypeDraft, setExamTypeDraft] = useState("");
  const [examDescriptionDraft, setExamDescriptionDraft] = useState("");
  const [examDateInput, setExamDateInput] = useState("");
  const [examTimeDraft, setExamTimeDraft] = useState("");
  const [isSavingExamEdit, setIsSavingExamEdit] = useState(false);
  const [selectedGroomingAppointment, setSelectedGroomingAppointment] = useState<(typeof resolvedGroomingAppointments)[number] | null>(null);
  const [isGroomingModalOpen, setIsGroomingModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [selectedStay, setSelectedStay] = useState<(StayHistoryRow & { petName: string }) | null>(null);
  const [selectedTerm, setSelectedTerm] = useState<PatientTerm | null>(null);
  const [isTermModalOpen, setIsTermModalOpen] = useState(false);
  const [isTermEditModalOpen, setIsTermEditModalOpen] = useState(false);
  const [isTermShareOpen, setIsTermShareOpen] = useState(false);
  const [isSendForSignatureOpen, setIsSendForSignatureOpen] = useState(false);
  const [isDeleteTermDialogOpen, setIsDeleteTermDialogOpen] = useState(false);
  const [termTitleDraft, setTermTitleDraft] = useState("");
  const [termContentDraft, setTermContentDraft] = useState("");
  const [termTemplateIdDraft, setTermTemplateIdDraft] = useState("");
  const [isSavingTermEdit, setIsSavingTermEdit] = useState(false);
  const [isSigningTerm, setIsSigningTerm] = useState(false);
  const [termDigitallySignedStoragePath, setTermDigitallySignedStoragePath] = useState<string | null>(null);
  const { deleteAttachment, updateAttachment } = useExamAttachments(petId, undefined, clinicId ?? undefined);
  const { data: termTemplates = [], isLoading: isLoadingTermTemplates } = useTermTemplates(
    false,
    clinicId ?? null,
  );
  const updatePatientTerm = useUpdatePatientTerm();
  const deletePatientTerm = useSoftDeletePatientTerm();
  const markTermPrinted = useMarkTermPrinted();
  const uploadTermSignedFile = useUploadTermSignedFile();
  const { data: documentTypes = [] } = useDocumentTypes();
  const { data: a1Certificate } = useUserA1Certificate();
  const hasCertificate = Boolean(a1Certificate);
  const { data: isVeterinarian } = useIsVeterinarian();
  const { data: currentVetProfile } = useQuery({
    queryKey: ["current-vet-profile-for-signature"],
    queryFn: async () => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("crmv_state,crmv_number,mapa_number,email")
        .eq("id", userId)
        .single();
      if (error) throw error;
      const profile = data as { crmv_state: string | null; crmv_number: number | null; mapa_number: string | null; email: string | null };
      // profiles.email isn't reliably kept in sync — the auth session's e-mail is
      // the one actually used to log in and receive Resend e-mails.
      return { ...profile, email: authData.user?.email ?? profile.email };
    },
    staleTime: 1000 * 60 * 5,
  });
  const currentVetCrmv = useMemo(() => {
    const state = currentVetProfile?.crmv_state?.trim() ?? "";
    const number = currentVetProfile?.crmv_number ? String(currentVetProfile.crmv_number) : "";
    return state && number ? `${state}-${number}` : number;
  }, [currentVetProfile]);
  const signPdf = useSignPdf();
  const [signingPrescriptionId, setSigningPrescriptionId] = useState<string | null>(null);
  const {
    data: historyFeedData,
    isLoading: isLoadingHistoryFeed,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePatientHistoryFeed({
    resolvedPetIds,
    historyClinicIds,
    includeGrooming,
    groomingTypeIds,
    selectedFilters,
  });

  const historyFeedPages = useMemo(() => historyFeedData?.pages ?? [], [historyFeedData]);
  const isLoadingRecords = isLoadingHistoryFeed;
  const isLoadingVaccinations = isLoadingHistoryFeed;
  const isLoadingExamAttachments = isLoadingHistoryFeed;
  const isLoadingPrescriptions = isLoadingHistoryFeed;
  const isLoadingPatientTerms = isLoadingHistoryFeed;
  const isLoadingHistoryAppointments = isLoadingHistoryFeed;
  const isLoadingGroomingAppointments = isLoadingHistoryFeed;

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: "240px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  type PetOwnerPrintRow = {
    name: string;
    birth_date: string | null;
    gender: { name: string } | null;
    breed: { name: string } | null;
    owner: {
      id: string;
      name: string;
      document: string | null;
      document_type_id: string | null;
      gender_id: string | null;
      birth_date: string | null;
      profession: string | null;
      marketing_consent: boolean;
      lead_source_id: string | null;
      owners_phones: Array<{ phone_number: string; is_whatsapp: boolean | null; is_primary: boolean | null; deleted_at: string | null }> | null;
      owners_emails: Array<{ email: string; is_primary: boolean | null; deleted_at: string | null }> | null;
      owners_addresses: Array<{
        street: string | null;
        number: string | null;
        neighborhood: string | null;
        complement: string | null;
        zip_code: string | null;
        city_id: string | null;
        is_primary: boolean | null;
        deleted_at: string | null;
      }> | null;
    } | null;
  };

  const { data: petPrintData } = useQuery({
    queryKey: ["patient-history", "term-print-data", petId],
    queryFn: async () => {
      if (!petId) return null;
      const { data, error } = await supabase
        .from("pets")
        .select("name,birth_date,gender:pet_genders(name),breed:breeds(name),owner:owners(id,name,document,document_type_id,gender_id,birth_date,profession,marketing_consent,lead_source_id,owners_phones(phone_number,is_whatsapp,is_primary,deleted_at),owners_emails(email,is_primary,deleted_at),owners_addresses(street,number,neighborhood,complement,zip_code,city_id,is_primary,deleted_at))")
        .eq("id", petId)
        .single();
      if (error) throw error;
      return (data ?? null) as unknown as PetOwnerPrintRow | null;
    },
    enabled: Boolean(petId),
    staleTime: 1000 * 60 * 5,
  });

  const termPlaceholderValues = useMemo(() => {
    const owner = petPrintData?.owner;
    const ownerDocumentTypeName = owner?.document_type_id
      ? documentTypes.find((type) => type.id === owner.document_type_id)?.name ?? null
      : null;
    const rawOwnerDocument = owner?.document?.trim() ?? "";
    const ownerDocumentWithType =
      rawOwnerDocument && ownerDocumentTypeName
        ? `${ownerDocumentTypeName}: ${rawOwnerDocument}`
        : rawOwnerDocument;

    // Only prefill the signature dialog's CPF field when the owner's stored document
    // is actually a CPF — RESPONSAVEL_DOCUMENTO above keeps the "<Type>: <value>" label
    // format for other consumers.
    const ownerRawCpf = ownerDocumentTypeName?.toUpperCase() === "CPF" ? rawOwnerDocument : "";
    const ownerPrimaryEmail =
      (owner?.owners_emails ?? []).filter((e) => e.deleted_at == null).find((e) => e.is_primary)?.email ??
      (owner?.owners_emails ?? []).filter((e) => e.deleted_at == null)[0]?.email ??
      "";

    return {
      RESPONSAVEL_NOME: owner?.name ?? "",
      RESPONSAVEL_DOCUMENTO: ownerDocumentWithType,
      RESPONSAVEL_CPF_BRUTO: ownerRawCpf,
      RESPONSAVEL_EMAIL: ownerPrimaryEmail,
      ANIMAL_NOME: petPrintData?.name ?? "",
      ANIMAL_SEXO: petPrintData?.gender?.name ?? "",
      ANIMAL_RACA: petPrintData?.breed?.name ?? "",
      ANIMAL_NASCIMENTO: formatDate(petPrintData?.birth_date ?? null),
    };
  }, [documentTypes, petPrintData]);

  const ownerProfileForSignature = useMemo(() => {
    const owner = petPrintData?.owner;
    if (!owner) return null;
    return {
      id: owner.id,
      name: owner.name,
      gender_id: owner.gender_id,
      birth_date: owner.birth_date,
      profession: owner.profession,
      marketing_consent: owner.marketing_consent,
      lead_source_id: owner.lead_source_id,
      phones: (owner.owners_phones ?? [])
        .filter((p) => p.deleted_at == null)
        .map((p) => ({
          phone_number: p.phone_number,
          is_whatsapp: p.is_whatsapp ?? false,
          is_primary: p.is_primary ?? false,
        })),
      addresses: (owner.owners_addresses ?? [])
        .filter((a) => a.deleted_at == null)
        .map((a) => ({
          zip_code: a.zip_code ?? "",
          street: a.street ?? "",
          number: a.number ?? "",
          complement: a.complement ?? "",
          neighborhood: a.neighborhood ?? "",
          city_id: a.city_id ?? "",
          is_primary: a.is_primary ?? false,
        })),
    };
  }, [petPrintData]);

  const selectedTermTemplate = useMemo(() => {
    const templateId = termTemplateIdDraft || selectedTerm?.template_id;
    if (!templateId) return null;
    return termTemplates.find((template) => template.id === templateId) ?? null;
  }, [selectedTerm?.template_id, termTemplateIdDraft, termTemplates]);

  const renderedSelectedTermPrintDoc = useMemo(() => {
    if (!selectedTerm) return null;
    const contentSource = selectedTerm.rendered_content?.trim()
      ? selectedTerm.rendered_content
      : selectedTerm.template_snapshot_content?.trim()
        ? renderTermWithTokenValues(selectedTerm.template_snapshot_content, termPlaceholderValues)
        : selectedTermTemplate
          ? renderTermWithTokenValues(selectedTermTemplate.content ?? "", termPlaceholderValues)
          : "";

    const titleSource = selectedTerm.rendered_title?.trim() || (selectedTerm.title ?? "");
    const titleHtmlSafe = sanitizeRichTextHtml(titleSource);
    const contentHtmlSafe = sanitizeRichTextHtml(contentSource);
    return {
      title: titleHtmlSafe,
      content: contentHtmlSafe,
    };
  }, [selectedTerm, selectedTermTemplate, termPlaceholderValues]);

  const petNameById = useMemo(() => {
    if (!isMultiPetMode) return new Map<string, string>();
    return new Map(
      (historyFeedPages[0]?.pets ?? []).map((pet: { id: string; name?: string | null }) => [
        pet.id,
        pet.name ?? "Paciente",
      ]),
    );
  }, [isMultiPetMode, historyFeedPages]);

  const petFilterOptions = useMemo(() => {
    if (!isMultiPetMode) return [];

    const dataPets = (historyFeedPages[0]?.pets ?? []) as Array<{ id?: string | null; name?: string | null }>;
    const byId = new Map<string, string>();

    dataPets.forEach((pet) => {
      if (!pet.id) return;
      byId.set(pet.id, pet.name?.trim() || petNameById.get(pet.id) || "Paciente");
    });

    resolvedPetIds.forEach((id) => {
      if (!byId.has(id)) {
        byId.set(id, petNameById.get(id) || "Paciente");
      }
    });

    return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
  }, [isMultiPetMode, historyFeedPages, petNameById, resolvedPetIds]);

  useEffect(() => {
    if (selectedPetId === "all") return;
    if (petFilterOptions.some((pet) => pet.id === selectedPetId)) return;
    setSelectedPetId("all");
  }, [petFilterOptions, selectedPetId]);

  const resolvedMedicalRecords = useMemo(
    () => historyFeedPages.flatMap((page) => page.medicalRecords),
    [historyFeedPages],
  );
  const resolvedVaccinations = useMemo(
    () => historyFeedPages.flatMap((page) => page.vaccinations),
    [historyFeedPages],
  );
  const resolvedExamAttachments = useMemo(
    () => historyFeedPages.flatMap((page) => page.attachments),
    [historyFeedPages],
  );
  const resolvedPrescriptions = useMemo(
    () => historyFeedPages.flatMap((page) => page.prescriptions),
    [historyFeedPages],
  );
  const resolvedPatientTerms = useMemo(
    () => historyFeedPages.flatMap((page) => page.terms),
    [historyFeedPages],
  );
  const resolvedGroomingAppointments = useMemo(
    () => historyFeedPages.flatMap((page) => page.grooming),
    [historyFeedPages],
  );
  const resolvedStayDays = useMemo(
    () => historyFeedPages.flatMap((page) => page.stayDays),
    [historyFeedPages],
  );
  const historyAppointments = useMemo(
    () =>
      historyFeedPages
        .flatMap((page) => page.appointments)
        .map(normalizeHistoryAppointmentRow)
        .filter((appointment): appointment is AppointmentWithDetails => Boolean(appointment)),
    [historyFeedPages],
  );
  const medicalTimelineRecords = resolvedMedicalRecords as MedicalRecordTimelineRow[];

  useEffect(() => {
    if (!selectedTerm) return;
    const refreshedSelectedTerm = (resolvedPatientTerms as PatientTerm[]).find((term) => term.id === selectedTerm.id);
    if (refreshedSelectedTerm && refreshedSelectedTerm !== selectedTerm) {
      setSelectedTerm(refreshedSelectedTerm);
    }
  }, [resolvedPatientTerms, selectedTerm]);

  const timelineItems = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    medicalTimelineRecords.forEach((record) => {
      const { iso, timestamp } = resolveTimestamp(record.consultation_date);
      items.push({
        id: `consultation-${record.id}`,
        sourceId: record.id,
        type: "consultation",
        date: iso || record.consultation_date,
        timestamp,
        medical_record_id: record.id,
        title: record.chief_complaint || "Consulta",
        description: truncate(
          [
            record.profiles?.full_name ? t("timeline.veterinarianName", { name: record.profiles.full_name }) : null,
            record.history ||
            record.presumptive_diagnosis ||
            record.definitive_diagnosis ||
            record.treatment,
          ]
            .filter(Boolean)
            .join(" • "),
        ),
        link: `/medical-records/${record.id}`,
        displayDate: formatTimelineDateTime(record.consultation_date),
        rawDate: record.consultation_date,
        rawData: record,
        petId: record.pet_id ?? null,
        petName: isMultiPetMode ? (petNameById.get(record.pet_id ?? "") ?? "Paciente") : null,
        veterinarianName: record.profiles?.full_name ?? null,
      });
    });

    historyAppointments.forEach((appointment) => {
      const eventDate = appointment.starts_at || appointment.created_at;
      const { iso, timestamp } = resolveTimestamp(eventDate);
      const matchingConsultation = findMatchingConsultationForAppointment(appointment, medicalTimelineRecords);
      const typeLabel = (appointment as { clinic_appointment_type?: { name?: string | null } | null }).clinic_appointment_type?.name ?? appointment.type ?? t("timeline.appointment");
      const statusLabel = APPOINTMENT_STATUS_LABEL[appointment.status] ?? appointment.status ?? null;
      const services = (appointment.appointment_items ?? [])
        .map((item) => item.item?.name || item.title_snapshot)
        .filter(Boolean)
        .slice(0, 3)
        .join(", ");
      const description = [
        statusLabel ? t("timeline.appointmentStatus", { status: statusLabel }) : null,
        services || null,
        appointment.notes || null,
      ].filter(Boolean).join(" • ");
      const petName =
        isMultiPetMode
          ? (petNameById.get(appointment.pet_id ?? "") ?? (appointment.pet?.name as string | undefined) ?? t("timeline.unknownPatient"))
          : null;

      items.push({
        id: `appointment-${appointment.id}`,
        sourceId: appointment.id,
        type: "appointment",
        date: iso || eventDate || "",
        timestamp,
        medical_record_id: matchingConsultation?.id ?? null,
        title: typeLabel,
        description: truncate(description),
        displayDate: formatTimelineDateTime(eventDate),
        rawDate: eventDate,
        rawData: appointment,
        petId: appointment.pet_id ?? null,
        petName,
        veterinarianName: (appointment.vet as { full_name?: string | null } | null)?.full_name ?? null,
      });
    });

    resolvedVaccinations.forEach((vaccination) => {
      const { iso, timestamp } = resolveTimestamp(vaccination.application_date ?? vaccination.created_at);
      const vaccineLabel =
        (vaccination as { item?: { name?: string | null } | null }).item?.name ??
        (typeof vaccination.item_id === "object" && vaccination.item_id !== null
          ? (vaccination.item_id as { name?: string })?.name
          : undefined) ??
        "Aplicação";
      items.push({
        id: `application-${vaccination.id}`,
        sourceId: vaccination.id,
        type: "vaccine",
        date: iso || vaccination.application_date || vaccination.created_at || "",
        timestamp,
        medical_record_id: vaccination.medical_record_id,
        title: vaccineLabel,
        description: vaccination.adverse_reaction || undefined,
        displayDate: formatTimelineDateTime(
          vaccination.application_date ?? vaccination.created_at,
        ),
        rawDate: vaccination.application_date ?? vaccination.created_at,
        rawData: vaccination,
        petId: vaccination.pet_id ?? null,
        petName: isMultiPetMode ? (petNameById.get(vaccination.pet_id ?? "") ?? "Paciente") : null,
      });
    });

    resolvedExamAttachments.forEach((attachment) => {
      let sortingDate: string | null | undefined = attachment.exam_date ?? attachment.created_at;
      if (attachment.exam_date && ISO_DATE_ONLY_REGEX.test(attachment.exam_date) && attachment.created_at) {
        const examDate = parseISODateOrDateTime(attachment.exam_date);
        const createdAt = parseISODateOrDateTime(attachment.created_at);
        if (examDate && createdAt) {
          const combined = new Date(examDate);
          combined.setHours(
            createdAt.getHours(),
            createdAt.getMinutes(),
            createdAt.getSeconds(),
            createdAt.getMilliseconds(),
          );
          sortingDate = combined.toISOString();
        }
      }
      const { iso, timestamp } = resolveTimestamp(sortingDate);
      const examTitle =
        attachment.type ||
        attachment.category ||
        t("timeline.documentFallback");
      const documentName =
        attachment.file_name?.trim() ||
        getFileBaseName(attachment.original_name || attachment.filename) ||
        t("timeline.documentFallback");
      const displayDate = attachment.exam_date && ISO_DATE_ONLY_REGEX.test(attachment.exam_date)
        ? `${formatTimelineDate(attachment.exam_date)}${formatTimelineTimeHHmm(attachment.created_at) ? ` às ${formatTimelineTimeHHmm(attachment.created_at)}` : ""}`
        : formatTimelineDateTime(attachment.exam_date ?? attachment.created_at);
      items.push({
        id: `exam-${attachment.id}`,
        sourceId: attachment.id,
        type: "exam",
        date: iso || attachment.exam_date || attachment.created_at || "",
        timestamp,
        medical_record_id: attachment.medical_record_id,
        title: examTitle,
        description: documentName,
        notes: truncate(attachment.description),
        displayDate,
        rawDate: attachment.exam_date ?? attachment.created_at,
        rawData: attachment,
        filePath: attachment.file_path,
        storageBucket: attachment.storage_bucket,
        petId: attachment.pet_id ?? null,
        petName: isMultiPetMode ? (petNameById.get(attachment.pet_id ?? "") ?? "Paciente") : null,
      });
    });

    resolvedPrescriptions.forEach((prescription) => {
      const { iso, timestamp } = resolveTimestamp(prescription.issued_at ?? prescription.created_at);
      const description =
        prescription.observations ||
        (prescription.type ? `Tipo: ${prescription.type}` : undefined);
      const prescriptionTitle = t(getPrescriptionTimelineLabelKey(prescription.type));
      items.push({
        id: `prescription-${prescription.id}`,
        sourceId: prescription.id,
        type: "prescription",
        date: iso || prescription.issued_at || prescription.created_at || "",
        timestamp,
        medical_record_id: prescription.medical_record_id,
        title: prescriptionTitle,
        description,
        displayDate: formatTimelineDateTime(prescription.issued_at ?? prescription.created_at),
        rawDate: prescription.issued_at ?? prescription.created_at,
        rawData: prescription,
        petId: prescription.pet_id ?? null,
        petName: isMultiPetMode ? (petNameById.get(prescription.pet_id ?? "") ?? "Paciente") : null,
      });
    });

    if (includeGrooming) {
      resolvedGroomingAppointments?.forEach((appointment) => {
        const eventDate = appointment.starts_at || appointment.created_at;
        const { iso, timestamp } = resolveTimestamp(eventDate);

        // Get services from order items
        const services = (appointment.order?.items ?? [])
          .map((item: { title_snapshot?: string; service?: { title?: string }; item?: { title?: string } }) =>
            item.title_snapshot || item.service?.title || item.item?.title
          )
          .filter(Boolean)
          .slice(0, 3)
          .join(", ");

        const description = [services, appointment.notes]
          .filter(Boolean)
          .join(" • ");

        items.push({
          id: `grooming-${appointment.id}`,
          sourceId: appointment.id,
          type: "grooming",
          date: iso || eventDate || "",
          timestamp,
          medical_record_id: null,
          title: "Banho e tosa",
          description: truncate(description),
          displayDate: formatTimelineDateTime(eventDate),
          rawDate: eventDate,
          rawData: appointment,
          petId: appointment.pet_id ?? null,
          petName: isMultiPetMode ? (petNameById.get(appointment.pet_id ?? "") ?? "Paciente") : null,
        });
      });
    }

    // Deduplicate by id (in case the API or cache returns the same term more than once)
    const seenTermIds = new Set<string>();
    resolvedPatientTerms.forEach((term) => {
      const normalizedTerm = term as PatientTerm;
      if (seenTermIds.has(normalizedTerm.id)) return;
      seenTermIds.add(normalizedTerm.id);

      const createdAt = resolveTimestamp(normalizedTerm.created_at);
      // One timeline entry per term (created only); no separate "printed" or "signed" entries to avoid duplicates
      items.push({
        id: `term-${normalizedTerm.id}`,
        sourceId: normalizedTerm.id,
        type: "term",
        date: createdAt.iso || normalizedTerm.created_at,
        timestamp: createdAt.timestamp,
        medical_record_id: normalizedTerm.medical_record_id,
        title: `${normalizedTerm.title}`,
        displayDate: formatTimelineDateTime(normalizedTerm.created_at),
        rawDate: normalizedTerm.created_at,
        rawData: normalizedTerm,
        petId: normalizedTerm.pet_id ?? null,
        petName: isMultiPetMode ? (petNameById.get(normalizedTerm.pet_id ?? "") ?? "Paciente") : null,
      });
    });

    resolvedStayDays.forEach((stayDay) => {
      const { title, detail, coverage } = formatStayHistoryLabel(stayDay, t);
      const cardDate = stayDay.checkInDate ?? stayDay.stayDate;
      const { iso, timestamp } = resolveTimestamp(`${cardDate}T12:00:00`);
      items.push({
        id: `stay-${stayDay.id}`,
        sourceId: stayDay.id,
        type: "stay",
        date: iso || cardDate,
        timestamp,
        medical_record_id: null,
        title,
        description: [detail, coverage].filter(Boolean).join("\n") || undefined,
        displayDate: formatTimelineDate(cardDate),
        rawDate: cardDate,
        rawData: stayDay,
        petId: stayDay.petId ?? null,
        petName: isMultiPetMode ? (petNameById.get(stayDay.petId ?? "") ?? "Paciente") : null,
      });
    });

    return items.sort((a, b) => b.timestamp - a.timestamp);
  }, [
    medicalTimelineRecords,
    historyAppointments,
    resolvedVaccinations,
    resolvedExamAttachments,
    resolvedPrescriptions,
    resolvedGroomingAppointments,
    includeGrooming,
    resolvedPatientTerms,
    resolvedStayDays,
    isMultiPetMode,
    petNameById,
    t,
  ]);

  const scopedTimelineItems = useMemo(() => {
    if (timeScope === "all") return timelineItems;
    const now = Date.now();
    if (timeScope === "past") {
      return timelineItems.filter((item) => item.timestamp <= now);
    }
    return timelineItems.filter((item) => item.timestamp > now);
  }, [timelineItems, timeScope]);

  const filteredItems = useMemo(() => {
    const petScopedItems =
      selectedPetId === "all"
        ? scopedTimelineItems
        : scopedTimelineItems.filter((item) => item.petId === selectedPetId);

    if (selectedFilters.includes("all")) {
      return petScopedItems;
    }
    const allowedTypes = selectedFilters.flatMap((f) => FILTER_MAP[f]);
    return petScopedItems.filter((item) => allowedTypes.includes(item.type));
  }, [scopedTimelineItems, selectedFilters, selectedPetId]);

  const shouldGroupByMedicalRecord = useMemo(() => {
    if (selectedFilters.includes("all")) return true;
    return selectedFilters.includes("consultations");
  }, [selectedFilters]);

  const recordsById = useMemo(() => {
    const lookup: Record<string, (typeof resolvedMedicalRecords)[number]> = {};
    resolvedMedicalRecords.forEach((record) => {
      lookup[record.id] = record;
    });
    return lookup;
  }, [resolvedMedicalRecords]);

  const groupedEntries = useMemo(() => {
    if (!shouldGroupByMedicalRecord) return [];
    const groups: Record<
      string,
      {
        items: TimelineItem[];
        timestamp: number;
      }
    > = {};
    filteredItems.forEach((item) => {
      if (!item.medical_record_id) return;
      if (!recordsById[item.medical_record_id]) return;
      const group = groups[item.medical_record_id] ?? { items: [], timestamp: 0 };
      group.items.push(item);
      group.timestamp = Math.max(group.timestamp, item.timestamp);
      groups[item.medical_record_id] = group;
    });
    return Object.entries(groups).map(([recordId, group]) => ({
      recordId,
      items: group.items.sort((a, b) => b.timestamp - a.timestamp),
      timestamp: group.timestamp,
      record: recordsById[recordId],
    }));
  }, [filteredItems, recordsById, shouldGroupByMedicalRecord]);

  const groupedRecordIds = useMemo(() => {
    return new Set(groupedEntries.map((group) => group.recordId));
  }, [groupedEntries]);

  const timelineEntries = useMemo(() => {
    const grouped = groupedEntries.map((group) => ({
      kind: "group" as const,
      group,
      date: group.timestamp,
    }));
    const standalone = filteredItems
      .filter((item) => !item.medical_record_id || !groupedRecordIds.has(item.medical_record_id))
      .map((item) => ({
        kind: "item" as const,
        item,
        date: item.timestamp,
      }));
    return [...grouped, ...standalone].sort((a, b) => b.date - a.date);
  }, [groupedEntries, filteredItems, groupedRecordIds]);

  const isLoading =
    isLoadingRecords ||
    isLoadingHistoryAppointments ||
    isLoadingVaccinations ||
    isLoadingExamAttachments ||
    isLoadingPrescriptions ||
    isLoadingGroomingAppointments ||
    isLoadingPatientTerms;

  const availableFilterOptions = useMemo(() => {
    const baseOptions = FILTER_OPTIONS.filter((option) => {
      if (option.value === "grooming") {
        return includeGrooming;
      }
      return true;
    });
    const stayFilter = getStayHistoryFilter(stayMode, t);
    return stayFilter ? [...baseOptions, stayFilter] : baseOptions;
  }, [includeGrooming, stayMode, t]);

  // Remove "grooming" from selected filters if includeGrooming becomes false
  useEffect(() => {
    if (!includeGrooming && selectedFilters.includes("grooming")) {
      setSelectedFilters((prev) => {
        const withoutGrooming = prev.filter((f) => f !== "grooming");
        return withoutGrooming.length > 0 ? withoutGrooming : ["all"];
      });
    }
  }, [includeGrooming, selectedFilters]);

  useEffect(() => {
    if (stayMode !== "none" || !selectedFilters.includes("stay")) return;
    setSelectedFilters((prev) => {
      const withoutStay = prev.filter((filter) => filter !== "stay");
      return withoutStay.length > 0 ? withoutStay : ["all"];
    });
  }, [selectedFilters, stayMode]);

  const toggleFilter = (filter: FilterType) => {
    if (filter === "all") {
      setSelectedFilters(["all"]);
    } else {
      setSelectedFilters((prev) => {
        const withoutAll = prev.filter((f) => f !== "all");
        if (withoutAll.includes(filter)) {
          const newFilters = withoutAll.filter((f) => f !== filter);
          return newFilters.length > 0 ? newFilters : ["all"];
        } else {
          return [...withoutAll, filter];
        }
      });
    }
  };

  const handleOpenExamFile = useCallback(async (item: TimelineItem) => {
    if (!item.filePath || !item.storageBucket) {
      toast.error(t("timeline.fileUnavailable"));
      return;
    }

    try {
      const storagePath =
        normalizeStoragePath(item.filePath, item.storageBucket) ?? item.filePath;

      if (storagePath.startsWith("http")) {
        window.open(storagePath, "_blank");
        return;
      }

      const { data, error } = await supabase.storage
        .from(item.storageBucket)
        .createSignedUrl(storagePath, 3600); // 1 hora de validade

      if (error) {
        toast.error(t("timeline.openFileError"));
        return;
      }

      // Abrir em nova aba
      window.open(data.signedUrl, "_blank");
    } catch (err) {
      console.error("Erro ao abrir arquivo:", err);
      toast.error(t("timeline.openFileError"));
    }
  }, [t]);

  const handleOpenTermFile = useCallback(async (term: PatientTerm) => {
    if (!term.signed_storage_bucket || !term.signed_storage_path) {
      toast.error(t("timeline.signedTermUnavailable"));
      return;
    }

    try {
      const storagePath =
        normalizeStoragePath(term.signed_storage_path, term.signed_storage_bucket) ?? term.signed_storage_path;

      if (storagePath.startsWith("http")) {
        window.open(storagePath, "_blank");
        return;
      }

      const { data, error } = await supabase.storage
        .from(term.signed_storage_bucket)
        .createSignedUrl(storagePath, 3600);

      if (error || !data?.signedUrl) {
        toast.error(t("timeline.openFileError"));
        return;
      }

      window.open(data.signedUrl, "_blank");
    } catch (err) {
      console.error("Erro ao abrir termo assinado:", err);
      toast.error(t("timeline.openFileError"));
    }
  }, [t]);

  const handleUploadSignedTermFile = useCallback(async (term: PatientTerm, file: File | null) => {
    if (!file || !clinicId || !petId) return;

    try {
      await uploadTermSignedFile.mutateAsync({
        termId: term.id,
        petId,
        clinicId,
        file,
      });
    } catch (error) {
      console.error("Erro ao anexar arquivo assinado pelo histórico:", error);
    }
  }, [clinicId, petId, uploadTermSignedFile]);

  const handleSignTermDigitally = useCallback(async () => {
    if (!selectedTerm || !renderedSelectedTermPrintDoc) return;
    setIsSigningTerm(true);
    try {
      const result = await signPdf.mutateAsync({
        resource_type: "patient_term",
        resource_id: selectedTerm.id,
        term_pet_id: petId ?? undefined,
      });

      setTermDigitallySignedStoragePath(result.storage_path);
      setSelectedTerm((current) => current ? { ...current } : current);
      toast.success(t("timeline.termSignedSuccess", { defaultValue: "Termo assinado digitalmente!" }));
    } catch (error) {
      console.error("Erro ao assinar termo digitalmente:", error);
    } finally {
      setIsSigningTerm(false);
    }
  }, [selectedTerm, renderedSelectedTermPrintDoc, signPdf, petId, t]);

  const handleDownloadSignedTerm = useCallback(async () => {
    const path = termDigitallySignedStoragePath
      ?? (selectedTerm as (PatientTerm & { digitally_signed_storage_path?: string | null }) | null)?.digitally_signed_storage_path;
    if (!path) return;
    try {
      const { data, error } = await supabase.storage.from("signed-pdfs").createSignedUrl(path, 60);
      if (error || !data?.signedUrl) throw error ?? new Error("url_failed");
      const response = await fetch(data.signedUrl);
      if (!response.ok) throw new Error("download_failed");
      const bytes = new Uint8Array(await response.arrayBuffer());
      downloadPdf(bytes, `termo_assinado_${selectedTerm?.id?.slice(0, 8) ?? "term"}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error(t("timeline.downloadSignedError", { defaultValue: "Erro ao baixar PDF assinado" }));
    }
  }, [termDigitallySignedStoragePath, selectedTerm, t]);

  const handleVaccinationClick = (item: TimelineItem) => {
    if (item.rawData && item.type === "vaccine") {
      setSelectedVaccination(item.rawData as (typeof resolvedVaccinations)[number]);
      setIsVaccinationModalOpen(true);
    }
  };

  const handlePrescriptionClick = (item: TimelineItem) => {
    if (item.rawData) {
      setSelectedPrescription(item.rawData as PrescriptionWithItems);
      setIsPrescriptionModalOpen(true);
    }
  };

  const handleExamClick = (item: TimelineItem) => {
    handleOpenExamFile(item);
  };

  const handleGroomingClick = (item: TimelineItem) => {
    if (item.rawData && item.type === "grooming") {
      setSelectedGroomingAppointment(item.rawData as (typeof resolvedGroomingAppointments)[number]);
      setIsGroomingModalOpen(true);
    }
  };

  const handleAppointmentClick = (item: TimelineItem) => {
    if (item.rawData && item.type === "appointment") {
      setSelectedAppointmentId(item.sourceId);
    }
  };

  const handleTermClick = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "term") return;
    const term = item.rawData as PatientTerm;
    setSelectedTerm(term);
    setTermTemplateIdDraft(term.template_id ?? "");
    setIsTermModalOpen(true);
  };

  useEffect(() => {
    if (hasHandledInitialOpenEvent.current) return;
    if (!initialOpenEvent?.sourceId || !initialOpenEvent?.type) return;
    if (timelineItems.length === 0) return;

    const targetItem = timelineItems.find(
      (item) => item.type === initialOpenEvent.type && item.sourceId === initialOpenEvent.sourceId,
    );
    if (!targetItem) return;

    hasHandledInitialOpenEvent.current = true;

    if (targetItem.type === "vaccine" && targetItem.rawData) {
      setSelectedVaccination(targetItem.rawData as (typeof resolvedVaccinations)[number]);
      setIsVaccinationModalOpen(true);
      return;
    }
    if (targetItem.type === "prescription" && targetItem.rawData) {
      setSelectedPrescription(targetItem.rawData as PrescriptionWithItems);
      setIsPrescriptionModalOpen(true);
      return;
    }
    if (targetItem.type === "exam") {
      void handleOpenExamFile(targetItem);
      return;
    }
    if (targetItem.type === "grooming" && targetItem.rawData) {
      setSelectedGroomingAppointment(targetItem.rawData as (typeof resolvedGroomingAppointments)[number]);
      setIsGroomingModalOpen(true);
      return;
    }
    if (targetItem.type === "appointment" && targetItem.rawData) {
      setSelectedAppointmentId(targetItem.sourceId);
      return;
    }
    if (targetItem.type === "term" && targetItem.rawData) {
      const term = targetItem.rawData as PatientTerm;
      setSelectedTerm(term);
      setTermTemplateIdDraft(term.template_id ?? "");
      setIsTermModalOpen(true);
      return;
    }
    if (targetItem.type === "consultation" && targetItem.link) {
      window.location.assign(targetItem.link);
    }
  }, [
    handleOpenExamFile,
    initialOpenEvent,
    resolvedGroomingAppointments,
    resolvedVaccinations,
    timelineItems,
  ]);

  const handleOpenVaccinationEdit = () => {
    if (!selectedVaccination) return;
    setIsVaccinationModalOpen(false);
    setIsVaccinationEditModalOpen(true);
  };

  const handleConfirmVaccinationDelete = async () => {
    if (!selectedVaccination?.id) return;

    try {
      await deleteVaccination.mutateAsync(selectedVaccination.id);
      setIsDeleteVaccinationDialogOpen(false);
      setIsVaccinationModalOpen(false);
      setIsVaccinationEditModalOpen(false);
      setSelectedVaccination(null);
    } catch (error) {
      console.error("Erro ao remover aplicação pelo histórico:", error);
    }
  };

  const handleOpenPrescriptionFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "prescription") return;
    setAutoOpenPrescriptionShare(false);
    setSelectedPrescription(item.rawData as PrescriptionWithItems);
    setIsPrescriptionModalOpen(true);
  };

  const handleOpenPrescriptionShareFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "prescription") return;
    setAutoOpenPrescriptionShare(true);
    setSelectedPrescription(item.rawData as PrescriptionWithItems);
    setIsPrescriptionModalOpen(true);
  };

  const handleDeletePrescriptionFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "prescription") return;
    setSelectedPrescription(item.rawData as PrescriptionWithItems);
    setIsDeletePrescriptionDialogOpen(true);
  };

  const handleEditPrescriptionFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "prescription") return;
    setAutoOpenPrescriptionShare(false);
    setSelectedPrescription(item.rawData as PrescriptionWithItems);
    setIsPrescriptionEditModalOpen(true);
  };

  const handleSignPrescriptionFromTimeline = async (prescription: PrescriptionWithItems) => {
    setSigningPrescriptionId(prescription.id);
    try {
      const result = await signPdf.mutateAsync({
        resource_type: "prescription",
        resource_id: prescription.id,
        pet_id: petId,
      });
      const secretCode = result.secret_code ?? result.access_code;
      if (secretCode) {
        toast.success(
          t("timeline.signSuccessWithCode", {
            defaultValue: "Receita assinada!",
            code: secretCode,
          }),
        );
      }
    } finally {
      setSigningPrescriptionId(null);
    }
  };

  const handleDownloadSignedPrescription = async (prescription: PrescriptionWithItems) => {
    const p = prescription as PrescriptionWithItems & {
      signed_storage_path?: string | null;
      signed_storage_bucket?: string | null;
    };
    if (!p.signed_storage_path) return;
    const bucket = p.signed_storage_bucket ?? "signed-pdfs";
    try {
      const { data: urlData, error: urlErr } = await supabase.storage
        .from(bucket)
        .createSignedUrl(p.signed_storage_path, 60);
      if (urlErr || !urlData?.signedUrl) throw urlErr ?? new Error("url_failed");
      window.open(urlData.signedUrl, "_blank");
    } catch (e) {
      console.error(e);
      toast.error(t("timeline.downloadSignedError", { defaultValue: "Erro ao baixar PDF assinado" }));
    }
  };

  const handleEditVaccinationFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "vaccine") return;
    setSelectedVaccination(item.rawData as (typeof resolvedVaccinations)[number]);
    setIsVaccinationEditModalOpen(true);
  };

  const handleDeleteVaccinationFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "vaccine") return;
    setSelectedVaccination(item.rawData as (typeof resolvedVaccinations)[number]);
    setIsDeleteVaccinationDialogOpen(true);
  };

  const handleDeleteExamFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "exam") return;
    setExamToDelete(item.rawData as (typeof resolvedExamAttachments)[number]);
  };

  const handleOpenTermFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "term") return;
    const term = item.rawData as PatientTerm;
    setSelectedTerm(term);
    setTermTemplateIdDraft(term.template_id ?? "");
    setIsTermModalOpen(true);
  };

  const handleEditTermFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "term") return;
    const term = item.rawData as PatientTerm & { digitally_signed_at?: string | null };
    if (term.digitally_signed_at) return;
    const template = term.template_id
      ? termTemplates.find((t) => t.id === term.template_id) ?? null
      : null;
    const contentSource = term.rendered_content?.trim()
      ? term.rendered_content
      : term.template_snapshot_content?.trim()
        ? renderTermWithTokenValues(term.template_snapshot_content, termPlaceholderValues)
        : template
          ? renderTermWithTokenValues(template.content ?? "", termPlaceholderValues)
          : "";
    setSelectedTerm(term);
    setTermTitleDraft((term.rendered_title?.trim() || term.title) ?? "");
    setTermContentDraft(contentSource);
    setTermTemplateIdDraft(term.template_id ?? "");
    setIsTermEditModalOpen(true);
  };

  const handleDeleteTermFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "term") return;
    const termData = item.rawData as PatientTerm & { digitally_signed_at?: string | null };
    setSelectedTerm(termData);
    setIsDeleteTermDialogOpen(true);
  };

  const getExamTitle = (attachment?: (typeof resolvedExamAttachments)[number] | null) => {
    if (!attachment) return "";
    if (attachment.file_name?.trim()) return attachment.file_name.trim();
    const fallbackName =
      attachment.original_name || attachment.filename || "";
    const trimmed = fallbackName.trim();
    if (!trimmed) return t("timeline.documentFallback");
    const lastDot = trimmed.lastIndexOf(".");
    return lastDot > 0 ? trimmed.slice(0, lastDot) : trimmed;
  };

  const handleEditExamFromItem = (item: TimelineItem) => {
    if (!item.rawData || item.type !== "exam") return;
    const attachment = item.rawData as (typeof resolvedExamAttachments)[number];
    const parsedExamDate = parseISODateOrDateTime(attachment.exam_date);
    const parsedCreatedAt = parseISODateOrDateTime(attachment.created_at);
    const datePart = parsedExamDate ?? parsedCreatedAt;
    const timePart = parsedCreatedAt ?? parsedExamDate;
    const combinedDateTime = datePart
      ? (() => {
        const combined = new Date(datePart);
        if (timePart) {
          combined.setHours(
            timePart.getHours(),
            timePart.getMinutes(),
            timePart.getSeconds(),
            timePart.getMilliseconds(),
          );
        }
        return combined;
      })()
      : null;

    setExamToEdit(attachment);
    setExamTitleDraft(getExamTitle(attachment));
    setExamTypeDraft(attachment.type || attachment.category || "Documento");
    setExamDescriptionDraft(attachment.description ?? "");
    setExamDateInput(combinedDateTime ? toISODateOnlyLocal(combinedDateTime) : "");
    setExamTimeDraft(combinedDateTime ? format(combinedDateTime, "HH:mm") : "");
  };

  const handleConfirmPrescriptionDelete = async () => {
    if (!selectedPrescription?.id) return;

    setIsDeletingPrescription(true);
    try {
      const { error } = await supabase
        .from("medical_prescriptions")
        .delete()
        .eq("id", selectedPrescription.id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["medical-prescriptions"] });
      await queryClient.invalidateQueries({ queryKey: ["prescription-with-pet", selectedPrescription.id] });
      toast.success(t("timeline.prescriptionDeleted"));
      setIsDeletePrescriptionDialogOpen(false);
      setIsPrescriptionModalOpen(false);
      setSelectedPrescription(null);
    } catch (error) {
      console.error("Erro ao remover receita pelo histórico:", error);
      toast.error(t("timeline.deletePrescriptionError"));
    } finally {
      setIsDeletingPrescription(false);
    }
  };

  const handleConfirmExamDelete = async () => {
    if (!examToDelete) return;

    setIsDeletingExam(true);
    try {
      await deleteAttachment(examToDelete);
      setExamToDelete(null);
    } catch (error) {
      console.error("Erro ao remover exame pelo histórico:", error);
    } finally {
      setIsDeletingExam(false);
    }
  };

  const handleSaveExamEdit = async () => {
    if (!examToEdit) return;
    const title = examTitleDraft.trim();
    if (!title) {
      toast.error(t("timeline.documentNameRequired"));
      return;
    }
    if (!examTimeDraft) {
      toast.error(t("timeline.documentTimeRequired"));
      return;
    }

    const examDateIso = examDateInput;
    const parsedExamDate = examDateIso ? parseISODateOnlyLocal(examDateIso) : null;
    if (!parsedExamDate) {
      toast.error(t("timeline.invalidDocumentDate"));
      return;
    }
    const [hours, minutes] = examTimeDraft.split(":");
    const parsedHours = Number.parseInt(hours ?? "", 10);
    const parsedMinutes = Number.parseInt(minutes ?? "", 10);
    if (
      Number.isNaN(parsedHours) ||
      Number.isNaN(parsedMinutes) ||
      parsedHours < 0 ||
      parsedHours > 23 ||
      parsedMinutes < 0 ||
      parsedMinutes > 59
    ) {
      toast.error(t("timeline.invalidDocumentTime"));
      return;
    }
    const parsedExamDateTime = new Date(parsedExamDate);
    parsedExamDateTime.setHours(parsedHours, parsedMinutes, 0, 0);

    setIsSavingExamEdit(true);
    try {
      await updateAttachment({
        attachmentId: examToEdit.id,
        name: title,
        type: examTypeDraft || "Documento",
        description: examDescriptionDraft.trim() ? examDescriptionDraft.trim() : null,
        examDate: examDateIso,
        occurredAt: parsedExamDateTime.toISOString(),
      });
      setExamToEdit(null);
      setExamTitleDraft("");
      setExamTypeDraft("");
      setExamDescriptionDraft("");
      setExamDateInput("");
      setExamTimeDraft("");
    } catch (error) {
      console.error("Erro ao editar exame pelo histórico:", error);
    } finally {
      setIsSavingExamEdit(false);
    }
  };

  const handleSaveTermEdit = async () => {
    if (!selectedTerm?.id) return;
    const title = termTitleDraft.trim();
    if (!title) {
      toast.error(t("timeline.termTitleRequired"));
      return;
    }
    const renderedTitle = sanitizeRichTextHtml(title);
    const renderedContent = sanitizeRichTextHtml(termContentDraft);

    setIsSavingTermEdit(true);
    try {
      await updatePatientTerm.mutateAsync({
        termId: selectedTerm.id,
        title,
        rendered_title: renderedTitle,
        rendered_content: renderedContent,
      });
      setSelectedTerm((current) =>
        current
          ? {
            ...current,
            title,
            rendered_title: renderedTitle,
            rendered_content: renderedContent,
          }
          : current,
      );
      setIsTermEditModalOpen(false);
    } catch (error) {
      console.error("Erro ao editar termo pelo histórico:", error);
    } finally {
      setIsSavingTermEdit(false);
    }
  };

  const handleConfirmTermDelete = async () => {
    if (!selectedTerm?.id) return;
    try {
      await deletePatientTerm.mutateAsync(selectedTerm.id);
      setIsDeleteTermDialogOpen(false);
      setIsTermModalOpen(false);
      setIsTermEditModalOpen(false);
      setSelectedTerm(null);
      setTermTitleDraft("");
      setTermTemplateIdDraft("");
    } catch (error) {
      console.error("Erro ao remover termo pelo histórico:", error);
    }
  };


  const renderEventCard = (
    item: TimelineItem,
    options?: { compact?: boolean; showTypeLabel?: boolean },
  ) => {
    const isClickable =
      item.link ||
      item.type === "vaccine" ||
      item.type === "prescription" ||
      item.type === "exam" ||
      item.type === "appointment" ||
      item.type === "grooming" ||
      item.type === "term" ||
      item.type === "stay";

    const handleClick = () => {
      if (item.type === "vaccine") {
        handleVaccinationClick(item);
      } else if (item.type === "prescription") {
        handlePrescriptionClick(item);
      } else if (item.type === "exam") {
        handleExamClick(item);
      } else if (item.type === "appointment") {
        handleAppointmentClick(item);
      } else if (item.type === "grooming") {
        handleGroomingClick(item);
      } else if (item.type === "term") {
        handleTermClick(item);
      } else if (item.type === "stay" && item.rawData) {
        setSelectedStay({
          ...(item.rawData as StayHistoryRow),
          petName: item.petName ?? t("timeline.unknownPatient"),
        });
      }
    };

    // Para consultas, mostrar informações adicionais
    const record = item.type === "consultation" && item.rawData
      ? item.rawData as MedicalRecordTimelineRow
      : null;
    const treatment = record?.treatment;
    const followUpDate = hideFutureDetails ? null : record?.follow_up_date;
    const veterinarianName = record?.profiles?.full_name?.trim() || null;
    const vaccination = item.type === "vaccine" && item.rawData
      ? item.rawData as (typeof resolvedVaccinations)[number]
      : null;
    const vaccinationNextApplicationName =
      (vaccination as { next_application_item?: { name?: string | null } | null } | null)
        ?.next_application_item?.name?.trim() || null;
    const vaccinationMeta = vaccination
      ? [
        vaccination.dose_number
          ? { label: t("timeline.dose"), value: String(vaccination.dose_number) }
          : null,
        vaccination.next_dose_date && !vaccination.next_dose_completed_at
          ? { label: t("timeline.nextDoseLabel"), value: formatDateBR(vaccination.next_dose_date) }
          : null,
        vaccinationNextApplicationName && !vaccination.next_dose_completed_at
          ? { label: t("timeline.nextApplication"), value: vaccinationNextApplicationName }
          : null,
      ].filter(Boolean) as Array<{ label: string; value: string }>
      : [];

    // Para receitas, obter tipo e resumo
    const prescription = item.type === "prescription" && item.rawData
      ? item.rawData as PrescriptionWithItems & { signed_at?: string | null }
      : null;

    // Para termos
    const termRaw = item.type === "term" && item.rawData
      ? item.rawData as PatientTerm & { digitally_signed_at?: string | null; digitally_signed_storage_path?: string | null }
      : null;
    const termHasUploadedFile = Boolean(termRaw?.signed_storage_bucket && termRaw?.signed_storage_path);

    const signedFlags = prescription
      ? getPatientTimelineSignedFlags({ type: "prescription", signedAt: prescription.signed_at })
      : termRaw
        ? getPatientTimelineSignedFlags({
            type: "term",
            digitallySignedAt: termRaw.digitally_signed_at ?? termRaw.digitally_signed_storage_path,
            hasUploadedFile: termHasUploadedFile,
          })
        : null;
    const isSignedPrescription = signedFlags?.showPrescriptionSigned ?? false;
    const termIsDigitallySigned = signedFlags?.showTermSigned ?? false;

    const examAttachment = item.type === "exam" && item.rawData
      ? item.rawData as (typeof resolvedExamAttachments)[number]
      : null;
    const typeLabel = item.type === "exam" ? item.title : TYPE_LABELS[item.type];
    const titleLabel = item.type === "exam" ? item.description : item.title;

    const getPrescriptionSummary = (prescription: PrescriptionWithItems) => {
      if (prescription.type === "exam" && prescription.exam_items && prescription.exam_items.length > 0) {
        const examNames = prescription.exam_items
          .slice(0, 3)
          .map((item) => item.exam_name)
          .filter(Boolean);
        if (examNames.length > 0) {
          return examNames.join(", ");
        }
      }

      if (prescription.items && prescription.items.length > 0) {
        const itemNames = prescription.items
          .slice(0, 3)
          .map(item => item.medication_name || (item as { name?: string }).name)
          .filter(Boolean);
        if (itemNames.length > 0) {
          return itemNames.join(", ");
        }
      }
      return prescription.observations || "Sem itens";
    };

    const renderItemActions = () => {
      const hasActions = ["vaccine", "prescription", "exam", "term"].includes(item.type);
      if (!hasActions || (isMultiPetMode && item.type !== "exam")) return null;

      return (
        <div
          className={cn(
            "transition-opacity",
            "opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100",
          )}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Abrir menu de ações</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {item.type === "vaccine" && (
                <>
                  <DropdownMenuItem onSelect={() => handleEditVaccinationFromItem(item)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => handleDeleteVaccinationFromItem(item)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir
                  </DropdownMenuItem>
                </>
              )}

              {item.type === "prescription" && (() => {
                const prescRaw = item.rawData as PrescriptionWithItems & {
                  signed_at?: string | null;
                  signed_storage_path?: string | null;
                } | null;
                const isSigned = Boolean(prescRaw?.signed_at);
                return (
                  <>
                    <DropdownMenuItem onSelect={() => handleOpenPrescriptionFromItem(item)}>
                      <Eye className="mr-2 h-4 w-4" />
                      {t("timeline.openPrescription", { defaultValue: "Abrir receita" })}
                    </DropdownMenuItem>

                    {!isSigned && (
                      <DropdownMenuItem onSelect={() => handleEditPrescriptionFromItem(item)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {t("prescriptions:sign.edit")}
                      </DropdownMenuItem>
                    )}

                    {/* Sign option: only when not signed and user has A1 certificate */}
                    {!isSigned && hasCertificate && prescRaw && (
                      <DropdownMenuItem
                        onSelect={() => handleSignPrescriptionFromTimeline(prescRaw)}
                        disabled={signingPrescriptionId === prescRaw.id}
                      >
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        {t("timeline.signPrescription", { defaultValue: "Assinar digitalmente" })}
                      </DropdownMenuItem>
                    )}

                    {(isSigned || hasCertificate) && (
                      <DropdownMenuItem onSelect={() => handleOpenPrescriptionShareFromItem(item)}>
                        <Send className="mr-2 h-4 w-4" />
                        {isSigned
                          ? t("timeline.resendPrescription", { defaultValue: "Enviar novamente" })
                          : t("timeline.reviewSignAndSendPrescription", { defaultValue: "Revisar, assinar e enviar" })}
                      </DropdownMenuItem>
                    )}

                    {/* Download signed PDF option */}
                    {isSigned && prescRaw?.signed_storage_path && (
                      <DropdownMenuItem onSelect={() => handleDownloadSignedPrescription(prescRaw)}>
                        <Download className="mr-2 h-4 w-4" />
                        {t("timeline.downloadSigned", { defaultValue: "Baixar PDF assinado" })}
                      </DropdownMenuItem>
                    )}

                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => handleDeletePrescriptionFromItem(item)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t("timeline.deletePrescription", { defaultValue: "Excluir" })}
                      </DropdownMenuItem>
                    </>
                  </>
                );
              })()}

              {item.type === "exam" && (
                <>
                  <DropdownMenuItem onSelect={() => handleExamClick(item)}>
                    <Eye className="mr-2 h-4 w-4" />
                    {t("timeline.openDocument")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => handleEditExamFromItem(item)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {t("timeline.editDocument")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => handleDeleteExamFromItem(item)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("timeline.deleteDocument")}
                  </DropdownMenuItem>
                </>
              )}

              {item.type === "term" && (
                <>
                  <DropdownMenuItem onSelect={() => handleOpenTermFromItem(item)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Ver termo
                  </DropdownMenuItem>
                  {!termIsDigitallySigned && (
                    <DropdownMenuItem onSelect={() => handleEditTermFromItem(item)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar
                    </DropdownMenuItem>
                  )}
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => handleDeleteTermFromItem(item)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("timeline.dialogs.deleteTerm")}
                    </DropdownMenuItem>
                  </>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    };

    const content = (
      <div
        className={cn(
          "group rounded-lg border bg-background transition-colors",
          isClickable && "cursor-pointer hover:border-color-dark",
          options?.compact ? "p-3" : "p-4",
        )}
        onClick={isClickable && !item.link ? handleClick : undefined}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            {/* Tipo do evento + clínica de origem (consolidação multi-clínica) */}
            <div className="flex flex-wrap items-center gap-2">
              {options?.showTypeLabel && (
                <p className="text-xs text-muted-foreground">{typeLabel}</p>
              )}
              <ClinicTag clinicId={getTimelineItemClinicId(item)} />
            </div>

            {/* Título */}
            {titleLabel ? (
              <h3 className={cn("font-semibold text-foreground", options?.compact ? "text-sm" : "text-base")}>
                {titleLabel}
              </h3>
            ) : null}

            {isSignedPrescription ? (
              <div>
                <SignedIndicator />
              </div>
            ) : null}

            {(termIsDigitallySigned || termHasUploadedFile) ? (
              <div className="flex items-center gap-2">
                {termIsDigitallySigned && <SignedIndicator />}
                {termHasUploadedFile && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <UploadCloud className="h-3.5 w-3.5 text-blue-500" />
                    {t("timeline.termFileStored", { defaultValue: "Arquivo armazenado" })}
                  </span>
                )}
              </div>
            ) : null}

            {showPetName && item.petName ? (
              <div>
                <Badge variant="secondary" className="text-xs">
                  {item.petName}
                </Badge>
              </div>
            ) : null}
            {(item.type === "consultation" || item.type === "appointment") && item.veterinarianName ? (
              <p className="text-xs text-muted-foreground">
                {t("timeline.veterinarianName", { name: item.veterinarianName })}
              </p>
            ) : null}

            {/* Dados/Descrição */}
            {item.type === "consultation" && record ? (
              <div className="space-y-1">
                {treatment && (
                  <p className={cn("text-foreground", options?.compact ? "text-xs" : "text-sm")}>
                    {truncate(treatment, 200)}
                  </p>
                )}
                {followUpDate && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Retorno: {formatDateBR(followUpDate)}
                    </span>
                  </div>
                )}
              </div>
            ) : item.type === "prescription" && prescription ? (
              <div className="space-y-1">
                {getPrescriptionSummary(prescription) && (
                  <p
                    className={cn(
                      "text-muted-foreground whitespace-pre-line",
                      options?.compact ? "text-xs" : "text-sm",
                    )}
                  >
                    {getPrescriptionSummary(prescription)}
                  </p>
                )}
              </div>
            ) : item.type === "vaccine" && vaccination?.adverse_reaction ? (
              <div className="space-y-1">
                {vaccinationMeta.length > 0 ? (
                  <div className={cn("grid gap-2", vaccinationMeta.length > 1 ? "sm:grid-cols-2" : "grid-cols-1")}>
                    {vaccinationMeta.map((meta) => (
                      <div
                        key={`${item.id}-${meta.label}`}
                        className="rounded-md border border-border/70 bg-muted/20 px-3 py-2"
                      >
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                          {meta.label}
                        </p>
                        <p className={cn("text-foreground", options?.compact ? "text-xs" : "text-sm")}>
                          {meta.value}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : null}
                <p className="text-xs font-[600] text-muted-foreground">
                  {t("timeline.adverseReaction")}
                </p>
                <p
                  className={cn(
                    "text-muted-foreground whitespace-pre-line",
                    options?.compact ? "text-xs" : "text-sm",
                  )}
                >
                  {vaccination.adverse_reaction}
                </p>
              </div>
            ) : item.type === "vaccine" && vaccination ? (
              vaccinationMeta.length > 0 ? (
                <div className={cn("grid gap-2", vaccinationMeta.length > 1 ? "sm:grid-cols-3" : "grid-cols-1 w-fit")}>
                  {vaccinationMeta.map((meta) => (
                    <div
                      key={`${item.id}-${meta.label}`}
                      className="rounded-md border border-border/70 bg-muted/20 px-3 py-1.5"
                    >
                      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        {meta.label}
                      </p>
                      <p className={cn("text-foreground", options?.compact ? "text-xs" : "text-sm")}>
                        {meta.value}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null
            ) : item.type === "exam" && examAttachment ? (
              <div className="space-y-2">
                {item.notes ? (
                  <p
                    className={cn(
                      "text-muted-foreground whitespace-pre-line",
                      options?.compact ? "text-xs" : "text-sm",
                    )}
                  >
                    {item.notes}
                  </p>
                ) : null}
              </div>
            ) : item.description ? (
              <p
                className={cn(
                  "text-muted-foreground whitespace-pre-line",
                  options?.compact ? "text-xs" : "text-sm",
                )}
              >
                {item.description}
              </p>
            ) : null}
          </div>
          {renderItemActions()}
        </div>
      </div>
    );

    if (item.link && (item.type === "consultation" || item.type === "stay")) {
      return (
        <Link
          to={item.link}
          className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 rounded-lg"
        >
          {content}
        </Link>
      );
    }

    return content;
  };

  const renderGroup = (
    group: (typeof groupedEntries)[number],
    index: number,
    total: number,
  ) => {
    const consultationItem = group.items.find((item) => item.type === "consultation");
    const record = group.record;
    const nestedItems = group.items.filter((item) => item.type !== "consultation");

    const consultationTitle = record?.chief_complaint || "Consulta";
    const consultationDate = record?.consultation_date ?? group.items[0]?.date ?? "";
    const treatment = record?.treatment;
    const followUpDate = hideFutureDetails ? null : record?.follow_up_date;
    const veterinarianName = (record as MedicalRecordTimelineRow | null)?.profiles?.full_name?.trim() || null;
    const dateTime = formatTimelineDateTime(consultationDate);

    return (
      <div key={`group-${group.recordId}`} className="flex gap-2 relative">
        <div className={cn("flex flex-col items-center", index === 0 ? "before:hidden" : "before:content-[''] before:absolute before:left-[15.5px] before:top-[-25px] before:h-[25px] before:w-px before:bg-border")}>
          <div className="flex flex-col items-center gap-2">
            <div
              className={cn(
                "flex !h-8 !w-8 items-center justify-center rounded-full border-2 border-solid border-white shadow-soft-search",
                TIMELINE_TYPE_BADGE.consultation,
              )}
            >
              {getTimelineTypeIcon("consultation", { tone: "white", sizeClass: "h-4 w-4" })}
            </div>
          </div>
          {index !== total - 1 && <span className="flex-1 mt-0 min-h-[64px] w-px bg-border" />}
        </div>
        <div className="flex-1 space-y-3">
          <p className="text-xs font-semibold text-center justify-self-start mt-2">
            {dateTime}
          </p>
          <div className="rounded-lg border hover:border-color-dark p-4 bg-white">
            {/* Cabeçalho da consulta */}
            <Link
              to={`/medical-records/${group.recordId}`}
              className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 rounded-lg"
            >
              <div className="flex items-start justify-between gap-4 mb-3 cursor-pointer border-foreground rounded-lg p-2 -m-2 ">
                <div className="flex-1 min-w-0 space-y-2">
                  <p className="text-xs text-muted-foreground">Consulta</p>
                  <h3 className="text-base font-semibold text-foreground line-clamp-3 whitespace-pre-wrap">
                    {consultationTitle}
                  </h3>
                  {showPetName && consultationItem?.petName ? (
                    <div>
                      <Badge variant="secondary" className="text-xs">
                        {consultationItem.petName}
                      </Badge>
                    </div>
                  ) : null}
                  {consultationItem?.veterinarianName ? (
                    <p className="text-xs text-muted-foreground">
                      {t("timeline.veterinarianName", { name: consultationItem.veterinarianName })}
                    </p>
                  ) : null}
                  {(treatment || followUpDate) && (
                    <div className="space-y-1">
                      {treatment && (
                        <p className="text-sm text-foreground">
                          {truncate(treatment, 200)}
                        </p>
                      )}
                      {followUpDate && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            Retorno: {formatDateBR(followUpDate)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Link>

            {/* Itens associados */}
            {nestedItems.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-border/50">
                <div className="space-y-2">
                  {nestedItems.map((item) => (
                    <div key={item.id}>
                      {renderEventCard(item, { compact: true, showTypeLabel: true })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStandalone = (item: TimelineItem, index: number, total: number) => {
    const dateTime = item.displayDate;

    return (
      <div key={item.id} className="flex gap-2 relative">
        <div className={cn("flex flex-col items-center", index === 0 ? "before:hidden" : "before:content-[''] before:absolute before:left-[15.5px] before:top-[-25px] before:h-[25px] before:w-px before:bg-border")}>
          <div className="flex flex-col items-center gap-2">
            <div
              className={cn(
                "flex !h-8 !w-8 items-center justify-center rounded-full border-2 border-solid border-white shadow-soft-search",
                TIMELINE_TYPE_BADGE[item.type],
              )}
            >
              {getTimelineTypeIcon(item.type, { tone: "white", sizeClass: "h-4 w-4" })}
            </div>
          </div>
          {index !== total - 1 && <span className="flex-1 mt-0 h-full w-px bg-border" />}
        </div>
        <div className="flex-1 space-y-3">
          <p className="text-xs font-semibold text-center justify-self-start mt-2">
            {dateTime}
          </p>
          {renderEventCard(item, { showTypeLabel: true })}
        </div>
      </div>
    );
  };

  const hasContent = timelineEntries.length > 0;
  const hasPetScope = resolvedPetIds.length > 0;

  if (isLoading) {
    return <PatientsTimelineLoading />;
  }

  if (!hasPetScope) {
    return (
      <div className="rounded-lg border border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
        {t("historyTab.emptyDefault")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between">
        <div className="overflow-x-auto pb-2 px-1 -mx-1 flowboard-scroll [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center gap-2 min-w-max mt-1">
            {availableFilterOptions.map((option) => {
              const isSelected = selectedFilters.includes(option.value);
              return (
                <Button
                  key={option.value}
                  variant="outline"
                  size="sm"
                  onClick={() => toggleFilter(option.value)}
                  className={cn(
                    "flex-shrink-0 whitespace-nowrap",
                    isSelected
                      ? "bg-orange-100 border-none text-primary shadow-sm hover:bg-secondary hover:text-primary"
                      : "",
                  )}
                >
                  {"label" in option
                    ? option.label
                    : option.value === "all"
                    ? t("historyTab.filters.all")
                    : option.value === "consultations"
                      ? t("historyTab.filters.consultations")
                      : option.value === "vaccines"
                        ? t("historyTab.filters.vaccines")
                        : option.value === "exams"
                          ? t("historyTab.filters.exams")
                          : option.value === "prescriptions"
                            ? t("historyTab.filters.prescriptions")
                            : option.value === "terms"
                              ? t("historyTab.filters.terms")
                              : t("historyTab.filters.grooming")}
                </Button>
              );
            })}
          </div>
        </div>
        {petFilterOptions.length > 1 ? (
          <div className="mt-2 lg:mt-0 w-full lg:w-64">
            <Select value={selectedPetId} onValueChange={setSelectedPetId}>
              <SelectTrigger>
                <SelectValue placeholder={t("historyTab.petFilter.label")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("historyTab.petFilter.all")}</SelectItem>
                {petFilterOptions.map((pet) => (
                  <SelectItem key={pet.id} value={pet.id}>
                    {pet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      {!hasContent && !isLoading && (
        <div className="rounded-lg border border-dashed border-border/80 p-6 text-center text-muted-foreground">
          <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">
            {t("historyTab.emptyTitle")}
          </p>
          <p className="text-sm">
            {selectedFilters.includes("all")
              ? t("historyTab.emptyDefault")
              : t("historyTab.emptyFiltered")}
          </p>
        </div>
      )}

      {hasContent && (
        <div className="space-y-6">
          {timelineEntries.map((entry, index) => {
            if (entry.kind === "group") {
              return renderGroup(entry.group, index, timelineEntries.length);
            }
            return renderStandalone(entry.item, index, timelineEntries.length);
          })}
        </div>
      )}

      {hasNextPage ? (
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isFetchingNextPage ? (
            <p className="text-sm text-muted-foreground">{t("timeline.loadingMore")}</p>
          ) : null}
        </div>
      ) : null}

      {selectedAppointmentId ? (
        <AppointmentDetailsDialog
          appointmentId={selectedAppointmentId}
          open={Boolean(selectedAppointmentId)}
          onOpenChange={(open) => {
            if (!open) setSelectedAppointmentId(null);
          }}
        />
      ) : null}

      {/* Modal de Aplicação */}
      <Dialog open={isVaccinationModalOpen} onOpenChange={setIsVaccinationModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("timeline.dialogs.applicationDetails")}</DialogTitle>
          </DialogHeader>
          {selectedVaccination && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Aplicação</p>
                  <p className="text-base font-semibold text-foreground">
                    {(typeof selectedVaccination.item_id === "object" && selectedVaccination.item_id !== null
                      ? (selectedVaccination.item_id as { name?: string })?.name
                      : undefined) ??
                      "Aplicação"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Tipo</p>
                  <Badge variant="outline">
                    {(selectedVaccination as { item?: { product_subtype?: { name?: string | null } | null } | null }).item?.product_subtype?.name?.trim() || "Vacina"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Data de Aplicação</p>
                  <p className="text-sm text-foreground">
                    {selectedVaccination.application_date
                      ? formatTimelineDateTime(selectedVaccination.application_date)
                      : formatTimelineDateTime(selectedVaccination.created_at)}
                  </p>
                </div>
                {selectedVaccination.dose_number && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Dose</p>
                    <p className="text-sm text-foreground">Dose {selectedVaccination.dose_number}</p>
                  </div>
                )}
                {selectedVaccination.batch_number && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Lote</p>
                    <p className="text-sm text-foreground">{selectedVaccination.batch_number}</p>
                  </div>
                )}
                {selectedVaccination.next_dose_date && !selectedVaccination.next_dose_completed_at && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Próxima Dose</p>
                    <p className="text-sm text-foreground">
                      {formatTimelineDateTime(selectedVaccination.next_dose_date)}
                    </p>
                    {(selectedVaccination as { next_application_item?: { name?: string | null } | null }).next_application_item?.name ? (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("timeline.nextApplicationLabel", { name: (selectedVaccination as { next_application_item?: { name?: string | null } | null }).next_application_item?.name })}
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
              {selectedVaccination.adverse_reaction && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <p className="text-sm font-semibold text-destructive">Reação Adversa</p>
                  </div>
                  <p className="text-sm text-foreground">{selectedVaccination.adverse_reaction}</p>
                </div>
              )}
              {selectedVaccination.notes && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Observações</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {selectedVaccination.notes}
                  </p>
                </div>
              )}
              <DialogFooter className="flex-col sm:flex-row sm:justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleOpenVaccinationEdit}>
                  Editar
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setIsDeleteVaccinationDialogOpen(true)}
                  disabled={deleteVaccination.isPending}
                >
                  {deleteVaccination.isPending ? "Removendo..." : "Excluir"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Banho e Tosa */}
      <Dialog open={isGroomingModalOpen} onOpenChange={setIsGroomingModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("timeline.dialogs.groomingDetails")}</DialogTitle>
          </DialogHeader>
          {selectedGroomingAppointment && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Status</p>
                  <p className="text-base font-semibold text-foreground">
                    {getGroomingStatusLabel(selectedGroomingAppointment.grooming_status || selectedGroomingAppointment.status)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Data e Hora</p>
                  <p className="text-sm text-foreground">
                    {formatTimelineDateTime(selectedGroomingAppointment.starts_at || selectedGroomingAppointment.created_at)}
                  </p>
                </div>
                {selectedGroomingAppointment.appointment_items &&
                  selectedGroomingAppointment.appointment_items.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Serviços</p>
                      <div className="space-y-1">
                        {selectedGroomingAppointment.appointment_items.map(
                          (item: GroomingOrderItem & { title_snapshot?: string; item?: { name?: string } }, index: number) => {
                            const serviceName = item.title_snapshot || item.item?.name;
                            if (!serviceName) return null;
                            return (
                              <p key={index} className="text-sm text-foreground">
                                • {serviceName}
                              </p>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}
                {selectedGroomingAppointment.notes && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Observações</p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">
                      {selectedGroomingAppointment.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Receita */}
      {selectedPrescription && (() => {
        const isSigned = Boolean((selectedPrescription as PrescriptionWithItems & { signed_at?: string | null }).signed_at);
        return (
          <div>
            <PrescriptionViewDialog
              key={selectedPrescription.id}
              open={isPrescriptionModalOpen}
              onOpenChange={(open) => {
                setIsPrescriptionModalOpen(open);
                if (!open) setAutoOpenPrescriptionShare(false);
              }}
              prescription={selectedPrescription}
              onDelete={() => {
                setIsPrescriptionModalOpen(false);
                setIsDeletePrescriptionDialogOpen(true);
              }}
              onEdit={
                !isSigned
                  ? () => {
                      setIsPrescriptionModalOpen(false);
                      setAutoOpenPrescriptionShare(false);
                      setIsPrescriptionEditModalOpen(true);
                    }
                  : undefined
              }
              isDeleting={isDeletingPrescription}
              onSign={hasCertificate && !isSigned
                ? () => handleSignPrescriptionFromTimeline(selectedPrescription)
                : undefined}
              isSigning={signingPrescriptionId === selectedPrescription.id}
              autoOpenShare={autoOpenPrescriptionShare}
            />
          </div>
        );
      })()}

      <ResponsiveDialog
        open={isPrescriptionEditModalOpen}
        onOpenChange={(open) => {
          setIsPrescriptionEditModalOpen(open);
          if (!open) {
            setAutoOpenPrescriptionShare(false);
          }
        }}
        noScrollViewport
        className="flex max-h-[90vh] max-w-6xl flex-col p-0"
      >
        <div className="shrink-0 border-b border-border/60 bg-white px-6 py-4">
          <DialogHeader className="text-left">
            <DialogTitle>{t("prescriptions:composer.editPrescription")}</DialogTitle>
          </DialogHeader>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {selectedPrescription ? (
            <PrescriptionDialogLayout
              petId={selectedPrescription.pet_id}
              medicalRecordId={selectedPrescription.medical_record_id}
            >
              <PrescriptionComposer
                petId={selectedPrescription.pet_id}
                clinicId={selectedPrescription.clinic_id}
                appointmentId={selectedPrescription.appointment_id ?? undefined}
                medicalRecordId={selectedPrescription.medical_record_id ?? undefined}
                initialPrescription={selectedPrescription}
                autoOpenPreviewOnCreate={false}
                onUpdated={() => {
                  setIsPrescriptionEditModalOpen(false);
                  setSelectedPrescription(null);
                }}
                isInsideDialog={true}
                footerContainer={prescriptionEditFooterEl}
              />
            </PrescriptionDialogLayout>
          ) : null}
        </div>
        <div className="shrink-0 border-t border-border/60 bg-white px-6 py-4">
          <div ref={setPrescriptionEditFooterEl} className="flex justify-end gap-2" />
        </div>
      </ResponsiveDialog>

      {/* Modal de Termo */}
      {selectedTerm && (() => {
        const termWithDigital = selectedTerm as PatientTerm & { digitally_signed_storage_path?: string | null };
        const isDigitallySigned = Boolean(termDigitallySignedStoragePath ?? termWithDigital.digitally_signed_storage_path);
        const requiredRoles = deriveRequiredSignatories(
          selectedTerm.template_snapshot_content?.trim() || selectedTerm.rendered_content || "",
        );
        const requiresResponsible = requiredRoles.includes("responsible");
        const requiresVet = requiredRoles.includes("vet");
        const vetBlockedReason = requiresVet
          ? isVeterinarian === false
            ? t("terms:signature.send.vetConfirm.notVeterinarian")
            : !currentVetCrmv
              ? t("terms:signature.send.vetConfirm.missingCrmv")
              : null
          : null;
        return (
          <TermViewDialog
            open={isTermModalOpen}
            onOpenChange={(open) => {
              setIsTermModalOpen(open);
              if (!open) {
                setSelectedTerm(null);
                setTermTemplateIdDraft("");
                setTermDigitallySignedStoragePath(null);
              }
            }}
            term={selectedTerm}
            renderedContent={renderedSelectedTermPrintDoc}
            printSettings={printSettings}
            isDigitallySigned={isDigitallySigned}
            hasCertificate={hasCertificate}
            isSigningTerm={isSigningTerm}
            signPdfPending={signPdf.isPending}
            onSign={() => void handleSignTermDigitally()}
            onDownloadSigned={() => void handleDownloadSignedTerm()}
            hasStoredFile={Boolean(selectedTerm.signed_storage_bucket && selectedTerm.signed_storage_path)}
            isUploadPending={uploadTermSignedFile.isPending}
            onViewStoredFile={() => void handleOpenTermFile(selectedTerm)}
            onUploadFile={(file) => void handleUploadSignedTermFile(selectedTerm, file)}
            canUpload={Boolean(clinicId && petId)}
            onPrinted={async () => {
              await markTermPrinted.mutateAsync({ termId: selectedTerm.id });
              setSelectedTerm((current) =>
                current ? { ...current, printed_at: new Date().toISOString() } : current,
              );
            }}
            isPrintPending={markTermPrinted.isPending}
            onShare={isDigitallySigned ? () => setIsTermShareOpen(true) : undefined}
            onSendForSignature={
              requiresResponsible && !vetBlockedReason ? () => setIsSendForSignatureOpen(true) : undefined
            }
            sendForSignatureDisabledReason={requiresResponsible && vetBlockedReason ? vetBlockedReason : undefined}
          />
        );
      })()}

      {selectedTerm && (
        <SendForSignatureDialog
          open={isSendForSignatureOpen}
          onOpenChange={setIsSendForSignatureOpen}
          term={selectedTerm}
          petId={selectedTerm.pet_id}
          vetName={(selectedTerm.resolved_values_json as Record<string, string> | null)?.USUARIO_NOME ?? ""}
          hasCertificate={hasCertificate}
          requiresVetSignature={deriveRequiredSignatories(
            selectedTerm.template_snapshot_content?.trim() || selectedTerm.rendered_content || "",
          ).includes("vet")}
          vetEmail={currentVetProfile?.email ?? ""}
          vetCrmv={currentVetCrmv || undefined}
          vetMapa={currentVetProfile?.mapa_number ?? undefined}
          prefill={{
            name: termPlaceholderValues.RESPONSAVEL_NOME || (petPrintData?.owner?.name ?? ""),
            cpf: termPlaceholderValues.RESPONSAVEL_CPF_BRUTO ?? "",
            email: termPlaceholderValues.RESPONSAVEL_EMAIL ?? "",
            phone:
              (petPrintData?.owner?.owners_phones ?? []).find((p) => p.deleted_at == null)?.phone_number ?? "",
          }}
          ownerId={petPrintData?.owner?.id ?? null}
          ownerProfile={ownerProfileForSignature}
          documentTypes={documentTypes}
        />
      )}

      {selectedTerm && (
        <TermShareDrawer
          open={isTermShareOpen}
          onOpenChange={setIsTermShareOpen}
          term={
            selectedTerm as PatientTerm & {
              digitally_signed_access_code?: string | null;
            }
          }
          ownerName={petPrintData?.owner?.name ?? null}
          ownerPhones={
            (petPrintData?.owner?.owners_phones ?? []).filter(
              (p) => p.deleted_at == null && p.phone_number,
            )
          }
          petName={petPrintData?.name ?? null}
        />
      )}

      {/* Modal de Edição de Termo */}
      <TermEditDialog
        open={isTermEditModalOpen}
        onOpenChange={(open) => {
          setIsTermEditModalOpen(open);
          if (!open) {
            setTermTitleDraft("");
            setTermContentDraft("");
            setTermTemplateIdDraft("");
          }
        }}
        templateName={selectedTermTemplate?.name ?? selectedTerm?.template_snapshot_name}
        titleDraft={termTitleDraft}
        onTitleChange={setTermTitleDraft}
        contentDraft={termContentDraft}
        onContentChange={setTermContentDraft}
        onSave={handleSaveTermEdit}
        isSaving={isSavingTermEdit}
      />
      {selectedStay ? (
        <FollowUpDialog
          pet={toHistoryStayPanelPet(selectedStay)}
          isoDate={selectedStay.stayDate}
          open
          onOpenChange={(open) => { if (!open) setSelectedStay(null); }}
        />
      ) : null}

      <DocumentEditDialog
        open={Boolean(examToEdit)}
        onOpenChange={(open) => {
          if (!open) {
            setExamToEdit(null);
            setExamTitleDraft("");
            setExamTypeDraft("");
            setExamDescriptionDraft("");
            setExamDateInput("");
            setExamTimeDraft("");
          }
        }}
        labels={{
          title: t("timeline.dialogs.editDocument"),
          documentName: t("timeline.documentName"),
          documentNamePlaceholder: t("timeline.placeholders.documentName"),
          documentDate: t("timeline.documentDate"),
          documentTime: t("timeline.documentTime"),
          observations: t("timeline.observations"),
          observationsPlaceholder: t("timeline.placeholders.documentNotes"),
          cancel: t("timeline.cancel"),
          save: t("timeline.save"),
          saving: t("timeline.saving"),
        }}
        name={examTitleDraft}
        onNameChange={setExamTitleDraft}
        type={examTypeDraft}
        onTypeChange={setExamTypeDraft}
        date={examDateInput}
        onDateChange={setExamDateInput}
        time={examTimeDraft}
        onTimeChange={setExamTimeDraft}
        description={examDescriptionDraft}
        onDescriptionChange={setExamDescriptionDraft}
        onSave={handleSaveExamEdit}
        isSaving={isSavingExamEdit}
      />

      {/* Modal de Edição de Aplicação */}
      {selectedVaccination && (
        <VaccinationFormDialog
          open={isVaccinationEditModalOpen}
          onOpenChange={setIsVaccinationEditModalOpen}
          petId={petId ?? ""}
          clinicId={clinicId ?? undefined}
          vaccination={selectedVaccination}
          showCreateAppointmentToggle
        />
      )}

      {/* Confirmação exclusão aplicação */}
      <AlertDialog open={isDeleteVaccinationDialogOpen} onOpenChange={setIsDeleteVaccinationDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("timeline.dialogs.deleteApplication")}</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta aplicação? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteVaccination.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmVaccinationDelete}
              disabled={deleteVaccination.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteVaccination.isPending ? "Removendo..." : "Excluir aplicação"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmação exclusão receita */}
      <AlertDialog open={isDeletePrescriptionDialogOpen} onOpenChange={setIsDeletePrescriptionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("timeline.dialogs.deletePrescription")}</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta receita? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingPrescription}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmPrescriptionDelete}
              disabled={isDeletingPrescription}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingPrescription ? "Removendo..." : "Excluir receita"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DocumentDeleteConfirmDialog
        open={Boolean(examToDelete)}
        onOpenChange={(open) => {
          if (!open) setExamToDelete(null);
        }}
        labels={{
          title: t("timeline.dialogs.deleteDocument"),
          description: t("timeline.deleteDocumentConfirmation"),
          cancel: t("timeline.cancel"),
          confirm: t("timeline.deleteDocument"),
          confirming: t("timeline.removingDocument"),
        }}
        onConfirm={handleConfirmExamDelete}
        isDeleting={isDeletingExam}
      />

      {/* Confirmação exclusão termo */}
      <AlertDialog open={isDeleteTermDialogOpen} onOpenChange={setIsDeleteTermDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("timeline.dialogs.deleteTerm")}</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este termo? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePatientTerm.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmTermDelete}
              disabled={deletePatientTerm.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePatientTerm.isPending ? "Removendo..." : t("timeline.dialogs.deleteTerm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PatientHistoryTimeline;
